import { DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ActivityLogFullModel } from 'src/app/models/activity-logs/activity-log-full.model';
import { GetActivityLogsRequestModel } from 'src/app/models/activity-logs/activity-log-request.model';
import { ActivityLogTypeModel } from 'src/app/models/activity-logs/activity-log-type.model';
import { EventTypeModel } from 'src/app/models/events/event-type.model';
import { EventModel } from 'src/app/models/events/event.model';
import { LearningPathModel } from 'src/app/models/learning-paths/learning-path.model';
import { PersonModel } from 'src/app/models/person/person.model';
import { SelectOption } from 'src/app/models/select-option.model';
import { TableColumn } from 'src/app/models/table-column.model';
import { ActivityLogService } from 'src/app/services/activity-log.service';
import { LoadingService } from 'src/app/services/loading.service';
import CustomStore from 'devextreme/data/custom_store';
import { debounceTime, distinctUntilChanged, firstValueFrom, Subject } from 'rxjs';
import DataSource from 'devextreme/data/data_source';

@Component({
  selector: 'app-activity-logs',
  standalone: false,
  templateUrl: './activity-logs.component.html',
  styleUrl: './activity-logs.component.scss',
})
export class ActivityLogsComponent {
  activityLogsDataSource!: any;

  allPersons: PersonModel[] = [];
  allEvents: EventModel[] = [];
  allLearningPaths: LearningPathModel[] = [];

  allPersonOptions: SelectOption[] = [];
  allEventOptions: SelectOption[] = [];
  allLearningPathOptions: SelectOption[] = [];

  selectedPersonIds: string[] = [];
  selectedEventIds: string[] = [];
  selectedLearningPathIds: string[] = [];

  eventId?: string | null;
  learningPathId?: string | null;
  personId?: string | null;
  eventType?: 'training' | 'course' | null;

  disableEventSelection: boolean = false;
  disableLearningPathSelection: boolean = false;

  private searchSubject = new Subject<string>();
  searchText: string = '';

  columns: TableColumn[] = [
    {
      caption: this.translateService.instant('COMMON.FIELDS.DATE'),
      dataField: 'date',
      dataType: 'string',
      disableFiltering: false,
      cellValue: (data: ActivityLogFullModel): any => {
        return new Date(data.date);
      },
      calculateSortValue: (data: ActivityLogFullModel): Date | string => {
        return new Date(data.date);
      },
      calculateDisplayValue: (data: ActivityLogFullModel): string => {
        return (
          new DatePipe(this.translateService.currentLang).transform(
            new Date(data.date),
            'dd.MM.yyyy'
          ) || ''
        );
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.CREATOR'),
      dataField: 'initiator',
      dataType: 'string',
      cssClass: 'width-200',
      disableFiltering: false,
      cellValue: (data: ActivityLogFullModel): string => {
        return data.initiator.firstName + ' ' + data.initiator.lastName
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.ACTIVITY'),
      dataField: 'type',
      dataType: 'string',
      disableFiltering: false,
      cellValue: (data: ActivityLogFullModel): string => {
        switch (data.type) {
          case ActivityLogTypeModel.EventInvitationSentByEmail:
            return this.translateService.instant(
              'ACTIVITY-LOG.ACTIVITY-LOG-TYPES.INVITATION-SENT'
            );
          case ActivityLogTypeModel.CertificateSentByEmail:
            return this.translateService.instant(
              'ACTIVITY-LOG.ACTIVITY-LOG-TYPES.CERTIFICATE-SENT'
            );
          case ActivityLogTypeModel.LearningPathInvitationSent:
            return this.translateService.instant(
              'ACTIVITY-LOG.ACTIVITY-LOG-TYPES.LEARNING-PATH-INVITATION-SENT'
            );
          case ActivityLogTypeModel.TrainerParticipationInvitationSent:
            return this.translateService.instant(
              'ACTIVITY-LOG.ACTIVITY-LOG-TYPES.TRAINER-PARTICIPATION-INVITATION-SENT'
            );
          case ActivityLogTypeModel.CourseManuallyCompleted:
            return this.translateService.instant(
              'ACTIVITY-LOG.ACTIVITY-LOG-TYPES.COURSE-MANUALLY-COMPLETED'
            );
          case ActivityLogTypeModel.ParticipantIddTimeManuallyChanged:
            return this.translateService.instant(
              'ACTIVITY-LOG.ACTIVITY-LOG-TYPES.IDD-TIME-MANUALLY-CHANGED'
            );
          default:
            return 'n.a.';
        }
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.EMPLOYEE'),
      dataField: 'person',
      dataType: 'string',
      cssClass: 'width-200',
      disableFiltering: false,
      cellValue: (data: ActivityLogFullModel): string => {
        return data.person.firstName + ' ' + data.person.lastName
      },
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.TYPE'),
      dataField: 'event.type',
      dataType: 'string',
      cssClass: 'width-200',
      disableFiltering: false,
      cellValue: (data: ActivityLogFullModel): string => {
        if (data.event) {
          const type = data.event.type;

          if (type === EventTypeModel.Normal) {
            return this.translateService.instant('COMMON.FIELDS.TRAINING');
          }

          if (type === EventTypeModel.Webinar) {
            return this.translateService.instant('COMMON.FIELDS.ELEARNING');
          }

          return 'n.a.';
        }

        if (data.learningPath) {
          return this.translateService.instant('COMMON.FIELDS.LEARNING-PATH');
        }

        return '';
      },
    },
  ];

  constructor(
    private activityLogService: ActivityLogService,
    private translateService: TranslateService,
    private loadingService: LoadingService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.loadActivityLogs();

    this.searchSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(search => {
        this.searchText = search;
        this.applyFilters();
      });
  }

  loadActivityLogs(): void {
    this.route.queryParams
      .subscribe(params => {
        const trainingId = params['trainingId'] ?? null;
        const courseId = params['courseId'] ?? null;
        const learningPathId = params['learningPathId'] ?? null;

        if (trainingId) {
          this.eventId = trainingId;
          this.eventType = 'training';
        } else if (courseId) {
          this.eventId = courseId;
          this.eventType = 'course';
        } else if (learningPathId) {
          this.learningPathId = learningPathId;
        } else {
          this.eventId = null;
          this.eventType = null;
        }

        const personId = params['personId'] ?? null;
        this.personId = personId;

        this.initActivityLogsDataSource();

        const request: GetActivityLogsRequestModel = {
          eventId: this.eventId,
          learningPathId: this.learningPathId,
          personId: this.personId,
        } as GetActivityLogsRequestModel;

        this.loadingService.load(
          this.activityLogService.getActivityLogFilterOptions(
            request
          )
        ).subscribe(options => {
          this.allPersons = options.persons;
          this.allEvents = options.events;
          this.allLearningPaths = options.learningPaths;
          this.updateAllOptions();
        });
      });
  }

  initActivityLogsDataSource() {
    this.activityLogsDataSource = new DataSource({
      store: new CustomStore({
        key: 'id',

        load: async (loadOptions: any) => {
          const pageIndex = loadOptions.take
            ? Math.floor(loadOptions.skip / loadOptions.take)
            : 0;

          const pageSize = loadOptions.take || 20;

          const result = await firstValueFrom(
            this.getActivityLogsObservable(pageIndex, pageSize)
          );

          return {
            data: result.data,
            totalCount: result.totalCount
          };
        }
      })
    });
  }

  private getActivityLogsObservable(pageIndex: number, pageSize: number) {
    const request: GetActivityLogsRequestModel = {
      pageIndex,
      pageSize,
      eventId: this.eventId,
      learningPathId: this.learningPathId,
      personId: this.personId,
      personIds: this.selectedPersonIds,
      eventIds: this.selectedEventIds,
      learningPathIds: this.selectedLearningPathIds,
      searchText: this.searchText,
    };
    return this.activityLogService.getActivityLogs(request);
  }

  applyFilters() {
    this.activityLogsDataSource.reload();
  }

  onPersonSelected(e: any) {
    const id = e.node.key;

    if (e.node.selected) {
      if (!this.selectedPersonIds.includes(id)) {
        this.selectedPersonIds = [...this.selectedPersonIds, id];
      }
    } else {
      this.selectedPersonIds = this.selectedPersonIds.filter(pid => pid !== id);
    }

    this.applyFilters();
  }

  onEventSelected(e: any) {
    const id = e.node.key;

    if (e.node.selected) {
      if (!this.selectedEventIds.includes(id)) {
        this.selectedEventIds = [...this.selectedEventIds, id];
      }
    } else {
      this.selectedEventIds = this.selectedEventIds.filter(eid => eid !== id);
    }

    this.disableLearningPathSelection = this.selectedEventIds.length > 0;
    this.applyFilters();
  }

  onLearningPathSelected(e: any) {
    const id = e.node.key;

    if (e.node.selected) {
      if (!this.selectedLearningPathIds.includes(id)) {
        this.selectedLearningPathIds = [...this.selectedLearningPathIds, id];
      }
    } else {
      this.selectedLearningPathIds = this.selectedLearningPathIds.filter(eid => eid !== id);
    }

    this.disableEventSelection = this.selectedLearningPathIds.length > 0;
    this.applyFilters();
  }

  onSearchInput(e: any): void {
    this.searchSubject.next(e.value ?? '');
  }

  private updateAllOptions(){
    this.allPersonOptions = this.allPersons.map(p => ({
      id: p.id,
      name: `${p.firstName} ${p.lastName}`,
      selected: this.selectedPersonIds.includes(p.id)
    }));
    this.allEventOptions = this.allEvents.map(e => ({
      id: e.id,
      name: e.name,
      selected: this.selectedEventIds.includes(e.id),
    }));
    this.allLearningPathOptions = this.allLearningPaths.map(e => ({
      id: e.id,
      name: e.name,
      selected: this.selectedLearningPathIds.includes(e.id),
    }));
  }

  navigateBack(): void {
    if (this.eventId) {
      if(this.eventType == 'training') {
        this.router.navigate(['/training', this.eventId]);
      } else {
        this.router.navigate(['/manage-elearning', this.eventId]);
      }
    } 
    else if(this.personId) {
      this.router.navigate(['/employee', 'edit', this.personId]);
    } 
    else if(this.learningPathId) {
      this.router.navigate(['/manage-learning-path', this.learningPathId]);
    }
  }
}
